/**
 * Date Calculation Utilities
 *
 * This file contains utility functions for performing mathematical operations on dates.
 * These functions CREATE NEW dates by performing arithmetic, adjustments, or computations.
 * Use these utilities when you need to:
 * - Add or subtract time periods to create new dates (e.g., date + 3 days = new date)
 * - Calculate differences between dates (e.g., duration between start and end)
 * - Compute age from a birth date (e.g., years, months, days old)
 * - Adjust dates to specific values (e.g., change hours to 14:00, set day to 15)
 * - Sort arrays of dates or timestamps in ascending/descending order
 * - Perform any math that results in a NEW date or duration value
 *
 * Key distinction: These functions return NEW dates or computed values, not properties of existing dates.
 */
import {
  DateTime as LuxonDateTime,
  type DateObjectUnits as LuxonDateObjectUnits,
  type DurationObjectUnits as LuxonDurationObjectUnits,
} from 'luxon'
import { isDateValid } from '@/utils/date/validation.js'
import {
  getCurrentTimestamp,
  getDateTime,
  getDateTimeISOFromTimestamp,
  getTodayDateTime,
  type WeekDayISONumbers,
} from '@/utils/date/getter.js'
import { parseISODate } from '@/utils/date/conversion.js'

/**
 * Adds a specified amount of time (months, days, hours) to a date and returns a new Date object.
 *
 * @param date - The starting date as a Date object or ISO string
 * @param amount - An object specifying the units to add (months, days, hours)
 * @returns A new JavaScript Date object with the time added
 *
 * @example
 * ```typescript
 * addToDate('2024-01-15', { days: 7 })
 * // Returns: Date object representing 2024-01-22
 *
 * addToDate(new Date('2024-01-15'), { months: 2, days: 10 })
 * // Returns: Date object representing 2024-03-25
 * ```
 */
export function addToDate(
  date: Date | string,
  amount: {
    months?: number
    weeks?: number
    days?: number
    hours?: number
    minutes?: number
  },
) {
  return getDateTime(date).plus(amount).toJSDate()
}

/**
 * Subtracts a specified amount of time (months, days, hours) from a date and returns a new Date object.
 *
 * @param date - The starting date as a Date object or ISO string
 * @param amount - An object specifying the units to subtract (months, days, hours)
 * @returns A new JavaScript Date object with the time subtracted
 *
 * @example
 * ```typescript
 * subtractToDate('2024-01-15', { days: 7 })
 * // Returns: Date object representing 2024-01-08
 *
 * subtractToDate(new Date('2024-03-25'), { months: 2, days: 10 })
 * // Returns: Date object representing 2024-01-15
 * ```
 */
export function subtractToDate(
  date: Date | string,
  amount: {
    months?: number
    weeks?: number
    days?: number
    hours?: number
    minutes?: number
  },
) {
  return getDateTime(date).minus(amount).toJSDate()
}

/**
 * Adjusts a given date or date string based on the provided adjustment settings.
 * Using function overload for extra type-safety
 * If the input value is a string, the function will return a string.
 * If the input value is a Date object, the function will return a Date object.
 *
 * @param {Date | string} value - The date to be adjusted. Can be a Date object or a string.
 * @param {LuxonDateObjectUnits} adjustmentSettings - An object containing the units to adjust the date by.
 * @returns {Date | string} - The adjusted date. The return type matches the type of the input date.
 */
export function adjustDateTime(
  value: string,
  adjustmentSettings: LuxonDateObjectUnits,
): string
export function adjustDateTime(
  value: Date,
  adjustmentSettings: LuxonDateObjectUnits,
): Date
export function adjustDateTime(
  value: Date | string,
  adjustmentSettings: LuxonDateObjectUnits,
): Date | string {
  const paramValue = typeof value === 'string' ? value : value.toISOString()
  const dateTime = LuxonDateTime.fromISO(paramValue, { setZone: true })

  // make the adjustment, keeping the return in the same type format
  const adjusted = dateTime.set(adjustmentSettings)
  return typeof value === 'string'
    ? (adjusted.toISO() ?? '')
    : adjusted.toJSDate()
}

/**
 * Calculates the duration between two dates and returns an object containing the number of full days and remaining hours.
 * @param {Date | string | number} start - The start date, which can be a Date object, a string, or a number.
 * @param {Date | string | number} end - The end date, which can be a Date object, a string, or a number.
 */
export function calculateDurationObject(
  start: Date | string | number,
  end: Date | string | number,
): { days: number; hours: number; minutes: number } {
  const startDate = LuxonDateTime.fromJSDate(new Date(start))
  const endDate = LuxonDateTime.fromJSDate(new Date(end))
  const luxonDuration = endDate.diff(startDate)

  const days = luxonDuration.as('days')
  const hours = luxonDuration.as('hours') % 24 // Get remaining hours after full days
  const minutes = luxonDuration.as('minutes') % 60 // Get remaining minutes after full hours

  return {
    days: Math.floor(days),
    hours: Math.floor(hours),
    minutes: Math.floor(minutes),
  }
}

/**
 * Calculates the age difference between two dates.
 *
 * @param {Date | string | number} startDate - The starting date from which to calculate the age.
 * @param {Date | string | number} [endDate] - The ending date for the calculation.
 * If not provided, the current date and time will be used.
 *
 * @returns {DurationObjectUnits | undefined} An object representing the age difference in years,
 * months, days, hours, minutes, and milliseconds.
 *
 * @example
 * // Calculate age from a specific date to now
 * const age = calculateAge('2000-01-01'); // considering 2024-08-08 as today
 * console.log(age); // { years: 24, months: 7, days: 7, hours: 12, minutes: 30, milliseconds: 500 }
 *
 * @example
 * // Calculate age between two specific dates
 * const age = calculateAge('2000-01-01', '2024-01-01');
 * console.log(age); // { years: 24, months: 0, days: 0, hours: 0, minutes: 0, milliseconds: 0 }
 */
export function calculateAge(
  startDate: Date | string | number,
  endDate?: Date | string | number,
) {
  if (!isDateValid(startDate)) {
    return undefined
  }

  const start = LuxonDateTime.fromISO(new Date(startDate).toISOString())
  const end = endDate
    ? LuxonDateTime.fromISO(new Date(endDate).toISOString())
    : LuxonDateTime.now()

  return end
    .diff(start, [
      'years',
      'months',
      'days',
      'hours',
      'minutes',
      'milliseconds',
    ] satisfies (keyof LuxonDurationObjectUnits)[])
    .toObject()
}

/**
 * Calculates the total number of months between two dates.
 * Returns the floored number of full months from `start` to `end`.
 * If `end` is before `start`, the result will be negative.
 *
 * @param {Date | string} start - The start date.
 * @param {Date | string} end - The end date.
 * @returns {number} The number of full months between the two dates.
 *
 * @example
 * ```typescript
 * calculateMonthsDifference('2024-01-15', '2024-06-20')
 * // Returns: 5
 *
 * calculateMonthsDifference('2024-06-20', '2024-01-15')
 * // Returns: -5
 * ```
 */
export function calculateMonthsDifference(
  start: Date | string,
  end: Date | string,
): number {
  const startDateTime = getDateTime(start)
  const endDateTime = getDateTime(end)
  return Math.trunc(endDateTime.diff(startDateTime, ['months']).months)
}

/**
 * Sorts an array of objects containing ISO date strings in descending order (latest first).
 * @param timestamps - Array of objects with a `timestamp` property (ISO string).
 * @returns The sorted array.
 */
export function sortTimestampsDescending<T extends { timestamp: string }>(
  timestamps: T[],
): T[] {
  return timestamps.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
}

/**
 * Generic, domain-agnostic recurrence frequency types.
 */
export type RecurrenceFrequency =
  | 'once'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'custom'

/**
 * Time unit for custom recurrence intervals.
 */
export type RecurrenceUnit = 'days' | 'weeks' | 'months' | 'years'

/**
 * Monthly recurrence option for determining which day of the month.
 */
export type MonthlyRecurrenceOption =
  | 'firstDay'
  | 'lastDay'
  | 'firstMonday'
  | 'specificDay'

/**
 * Configuration for calculating recurrence occurrences.
 * Note: startDate is passed as a separate parameter to both functions.
 */
export interface RecurrenceOptions {
  frequency: RecurrenceFrequency
  interval?: number
  unit?: RecurrenceUnit
  weekdays?: WeekDayISONumbers[]
  monthlyOption?: MonthlyRecurrenceOption
  monthlyDay?: number
  endDate?: string | null
  count?: number
  maxOccurrences?: number | null
}

/**
 * Calculates periodic occurrences for a recurrence pattern with support for
 * custom intervals, specific weekdays, end dates, and max occurrence limits.
 *
 * @param startDate - The start date as a Date object or ISO date string
 * @param options - Configuration for the recurrence calculation
 * @returns Array of ISO date strings representing each occurrence
 */
export function getPeriodicOccurrences(
  startDate: Date | string,
  options: RecurrenceOptions,
): string[] {
  const {
    frequency,
    interval = 1,
    unit = 'days',
    weekdays = [],
    monthlyOption = 'specificDay',
    monthlyDay: monthlyDayInput,
    endDate,
    count = 3,
    maxOccurrences,
  } = options

  if (!isDateValid(startDate)) {
    return []
  }

  const startDateTime = getDateTime(startDate)
  const monthlyDay = monthlyDayInput ?? startDateTime.day

  let limit = count
  if (maxOccurrences !== null && maxOccurrences !== undefined) {
    limit = Math.min(count, maxOccurrences)
  }

  const endDateLimit = endDate ? getDateTime(endDate).endOf('day') : null
  const occurrences: string[] = []

  // For weekly frequency with specific days, find occurrences on those days
  if (
    (frequency === 'weekly' ||
      frequency === 'biweekly' ||
      frequency === 'custom') &&
    weekdays &&
    weekdays.length > 0
  ) {
    const sortedWeekdays = [...weekdays].sort((a, b) => a - b)
    const startTime = {
      hour: startDateTime.hour,
      minute: startDateTime.minute,
      second: startDateTime.second,
      millisecond: startDateTime.millisecond,
    }
    let weekStart = startDateTime.startOf('week')
    let collected = 0

    const weekInterval = frequency === 'biweekly' ? 2 : interval
    const maxWeeks = limit * weekInterval + 10
    for (let week = 0; week < maxWeeks && collected < limit; week++) {
      for (const targetDay of sortedWeekdays) {
        if (collected >= limit) {
          break
        }

        const candidate = weekStart.set({ weekday: targetDay, ...startTime })

        if (candidate >= startDateTime) {
          if (endDateLimit && candidate > endDateLimit) {
            return occurrences
          }
          occurrences.push(candidate.toISO() ?? '')
          collected++
        }
      }

      weekStart = weekStart.plus({ weeks: weekInterval })
    }

    return occurrences
  }

  // Handle frequency-specific logic
  switch (frequency) {
    case 'once':
      return startDateTime > getTodayDateTime()
        ? [startDateTime.toISO() ?? '']
        : []

    case 'daily': {
      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ days: 1 })
      }
      return occurrences
    }

    case 'weekdays': {
      let currentDate = startDateTime
      while (occurrences.length < limit) {
        if (endDateLimit && currentDate > endDateLimit) {
          break
        }
        if (currentDate.weekday !== 6 && currentDate.weekday !== 7) {
          occurrences.push(currentDate.toISO() ?? '')
        }
        currentDate = currentDate.plus({ days: 1 })
      }
      return occurrences
    }

    case 'weekly': {
      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ weeks: interval })
      }
      return occurrences
    }

    case 'biweekly': {
      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ weeks: 2 })
      }
      return occurrences
    }

    case 'monthly': {
      const getMonthlyDate = (date: LuxonDateTime) => {
        switch (monthlyOption) {
          case 'firstDay':
            return date.startOf('month')
          case 'lastDay':
            return date.set({ day: date.daysInMonth! })
          case 'firstMonday': {
            const firstDay = date.startOf('month')
            let candidate = firstDay
            while (candidate.weekday !== 1) {
              candidate = candidate.plus({ days: 1 })
            }
            return candidate
          }
          case 'specificDay':
          default: {
            const daysInMonth = date.daysInMonth!
            const dayToSet = Math.min(monthlyDay, daysInMonth)
            return date.set({ day: dayToSet })
          }
        }
      }

      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        const monthDate = getMonthlyDate(iterDate)
        if (endDateLimit && monthDate > endDateLimit) {
          break
        }
        occurrences.push(monthDate.toISO() ?? '')
        iterDate = iterDate.plus({ months: 1 })
      }
      return occurrences
    }

    case 'quarterly': {
      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ months: 3 })
      }
      return occurrences
    }

    case 'yearly': {
      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ years: 1 })
      }
      return occurrences
    }

    case 'custom': {
      const luxonUnit =
        unit === 'days'
          ? 'days'
          : unit === 'weeks'
            ? 'weeks'
            : unit === 'months'
              ? 'months'
              : 'years'

      if (unit === 'months') {
        const getMonthlyDate = (date: LuxonDateTime) => {
          switch (monthlyOption) {
            case 'firstDay':
              return date.startOf('month')
            case 'lastDay':
              return date.set({ day: date.daysInMonth! })
            case 'firstMonday': {
              const firstDay = date.startOf('month')
              let candidate = firstDay
              while (candidate.weekday !== 1) {
                candidate = candidate.plus({ days: 1 })
              }
              return candidate
            }
            case 'specificDay':
            default: {
              const daysInMonth = date.daysInMonth!
              const dayToSet = Math.min(monthlyDay, daysInMonth)
              return date.set({ day: dayToSet })
            }
          }
        }

        let iterDate = startDateTime
        for (let index = 0; index < limit; index++) {
          const monthDate = getMonthlyDate(iterDate)
          if (endDateLimit && monthDate > endDateLimit) {
            break
          }
          occurrences.push(monthDate.toISO() ?? '')
          iterDate = iterDate.plus({ months: interval })
        }
        return occurrences
      }

      let iterDate = startDateTime
      for (let index = 0; index < limit; index++) {
        if (endDateLimit && iterDate > endDateLimit) {
          break
        }
        occurrences.push(iterDate.toISO() ?? '')
        iterDate = iterDate.plus({ [luxonUnit]: interval })
      }
      return occurrences
    }

    default:
      return []
  }
}

/**
 * Calculates the next occurrence of a recurrence schedule based on frequency options.
 *
 * @param startDate - The schedule start date as a Date object or ISO format string
 * @param options - Frequency configuration
 * @returns The next occurrence date as an ISO string, or empty string if unable to calculate
 */
export function getNextRecurrence(
  startDate: Date | string,
  options: RecurrenceOptions,
): string {
  if (!isDateValid(startDate)) {
    return ''
  }

  const now = getTodayDateTime()
  const scheduleStart = getDateTime(startDate)

  if (scheduleStart > now) {
    return scheduleStart.toISO() ?? ''
  }

  const {
    frequency,
    interval = 1,
    unit = 'days',
    weekdays = [],
    monthlyOption = 'specificDay',
    monthlyDay: monthlyDayInput,
  } = options
  const monthlyDay = monthlyDayInput ?? scheduleStart.day

  const maxIterations = 10000
  const advanceFromStart = (duration: Partial<LuxonDurationObjectUnits>) => {
    let next = scheduleStart
    let iterations = 0
    while (next <= now && iterations < maxIterations) {
      next = next.plus(duration)
      iterations++
    }
    return next.toISO() ?? ''
  }

  switch (frequency) {
    case 'once':
      return scheduleStart > now ? (scheduleStart.toISO() ?? '') : ''

    case 'daily':
      return advanceFromStart({ days: 1 })

    case 'weekdays': {
      let next = scheduleStart
      let iterations = 0
      while (
        (next <= now || next.weekday === 6 || next.weekday === 7) &&
        iterations < maxIterations
      ) {
        next = next.plus({ days: 1 })
        iterations++
      }
      return next.toISO() ?? ''
    }

    case 'weekly': {
      if (weekdays.length === 0) {
        return advanceFromStart({ weeks: 1 })
      }
      const sortedWeekdays = [...weekdays].sort((a, b) => a - b)
      let next = scheduleStart
      let iterations = 0
      while (
        (next <= now ||
          !sortedWeekdays.includes(next.weekday as WeekDayISONumbers)) &&
        iterations < maxIterations
      ) {
        next = next.plus({ days: 1 })
        iterations++
      }
      return next.toISO() ?? ''
    }

    case 'biweekly':
      return advanceFromStart({ weeks: 2 })

    case 'monthly': {
      const getMonthlyDate = (date: LuxonDateTime) => {
        switch (monthlyOption) {
          case 'firstDay':
            return date.startOf('month')
          case 'lastDay':
            return date.set({ day: date.daysInMonth! })
          case 'firstMonday': {
            const firstDay = date.startOf('month')
            let candidate = firstDay
            while (candidate.weekday !== 1) {
              candidate = candidate.plus({ days: 1 })
            }
            return candidate
          }
          case 'specificDay':
          default: {
            const daysInMonth = date.daysInMonth!
            const dayToSet = Math.min(monthlyDay, daysInMonth)
            return date.set({ day: dayToSet })
          }
        }
      }

      let next = scheduleStart
      let iterations = 0
      while (getMonthlyDate(next) <= now && iterations < maxIterations) {
        next = next.plus({ months: 1 })
        iterations++
      }
      return getMonthlyDate(next).toISO() ?? ''
    }

    case 'quarterly':
      return advanceFromStart({ months: 3 })

    case 'yearly':
      return advanceFromStart({ years: 1 })

    case 'custom': {
      const unitMap: Record<RecurrenceUnit, keyof LuxonDurationObjectUnits> = {
        days: 'days',
        weeks: 'weeks',
        months: 'months',
        years: 'years',
      }
      const luxonUnit = unitMap[unit]
      if (!luxonUnit) {
        return ''
      }

      if (unit === 'weeks' && weekdays.length > 0) {
        const sortedWeekdays = [...weekdays].sort((a, b) => a - b)
        let weekStart = scheduleStart.startOf('week')
        let iterations = 0

        while (iterations < maxIterations) {
          for (const targetDay of sortedWeekdays) {
            const candidate = weekStart.set({
              weekday: targetDay as WeekDayISONumbers,
              hour: scheduleStart.hour,
              minute: scheduleStart.minute,
              second: scheduleStart.second,
            })
            if (candidate > now && candidate >= scheduleStart) {
              return candidate.toISO() ?? ''
            }
          }
          weekStart = weekStart.plus({ weeks: interval })
          iterations++
        }
        return ''
      }

      if (unit === 'months') {
        const getMonthlyDate = (date: LuxonDateTime) => {
          switch (monthlyOption) {
            case 'firstDay':
              return date.startOf('month')
            case 'lastDay':
              return date.set({ day: date.daysInMonth! })
            case 'firstMonday': {
              const firstDay = date.startOf('month')
              let candidate = firstDay
              while (candidate.weekday !== 1) {
                candidate = candidate.plus({ days: 1 })
              }
              return candidate
            }
            case 'specificDay':
            default: {
              const daysInMonth = date.daysInMonth!
              const dayToSet = Math.min(monthlyDay, daysInMonth)
              return date.set({ day: dayToSet })
            }
          }
        }

        let next = scheduleStart
        let iterations = 0
        while (getMonthlyDate(next) <= now && iterations < maxIterations) {
          next = next.plus({ months: interval })
          iterations++
        }
        return getMonthlyDate(next).toISO() ?? ''
      }

      return advanceFromStart({ [luxonUnit]: interval })
    }

    default:
      return ''
  }
}

/**
 * Calculates the next available time slot by rounding the current time up to the nearest
 * interval boundary. Useful for scheduling forms where selections must align to fixed intervals.
 *
 * @param intervalMinutes - The interval size in minutes (e.g., 15, 30, 60). Defaults to 30.
 * @returns An object with the ISO string, date, and time of the next available slot
 *
 * @example
 * ```typescript
 * // If current time is 14:17
 * getNextAvailableTimeSlot(30)
 * // Returns: { iso: '2026-03-30T14:30:00.000Z', date: '2026-03-30', time: '14:30' }
 *
 * // If current time is 14:30 exactly
 * getNextAvailableTimeSlot(30)
 * // Returns: { iso: '2026-03-30T14:30:00.000Z', date: '2026-03-30', time: '14:30' }
 *
 * // If current time is 14:05
 * getNextAvailableTimeSlot(15)
 * // Returns: { iso: '2026-03-30T14:15:00.000Z', date: '2026-03-30', time: '14:15' }
 * ```
 */
export function getNextAvailableTimeSlot(intervalMinutes = 30) {
  const nowTimestamp = getCurrentTimestamp()
  const intervalMilliseconds = intervalMinutes * 60 * 1000
  const nextSlotTimestamp =
    Math.ceil(nowTimestamp / intervalMilliseconds) * intervalMilliseconds
  const nextSlotISO = getDateTimeISOFromTimestamp(nextSlotTimestamp)
  const { date, time } = parseISODate(nextSlotISO)

  return { iso: nextSlotISO, date, time }
}
